//#region libraries
import React, { useEffect, useMemo, useState } from "react";

// bootstrap
import { Row, Col } from "react-bootstrap";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectApprovedEmployee,
  getEmployeeList,
  resetAssignEmployeesWorkZoneFlags,
  selectAssignEmployeesWorkZoneStatus,
  selectRemoveWorkzoneEmployeesStatus,
  resetRemoveWorkzoneEmployeesFlags,
  resetAddEmployeeForm
} from "_redux/slices/timeline/employees";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";

// custom components
import EmployeesListCard from "./employees-list-card";
import AddEmployeeForm from "./add-employee-form";
import EmployeeStatusFilter from "./employee-status-filter";
import EmployeeRoleFilter from "./employee-role-filter";
import EmployeeZoneFilter from "./employee-zone-filter";

import { Title as CustomTitle, SearchBar } from "_atoms";
import { TableHeader, ActionsModal } from "_molecules";

// styles
import { Spacing } from "_styles";

// functions
import { searchEmployeeName } from "_utils/functions";

//#endregion

const EmployeeSettings = () => {

  //#region redux
  const dispatch = useDispatch();

  const dbUser = useSelector(selectFirebaseInfo);
  const approvedEmployees = useSelector(selectApprovedEmployee);
  const assignEmployeesWorkZoneStatus = useSelector(selectAssignEmployeesWorkZoneStatus);
  const removeWorkzoneEmployeesStatus = useSelector(selectRemoveWorkzoneEmployeesStatus);
  //#endregion

  //#region states
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  //#endregion

  //#region use effects

  // on render use effect get employee list
  useEffect(() => {
    if(dbUser){
      // get the list of employees from the database
      dispatch(getEmployeeList(dbUser?.details?.authority));
    }
  }, []);

  //#endregion

  //#region functions

  // filter employees based on search and filters
  const filteredEmployees = useMemo(() => {
    if (!Array.isFullArray(approvedEmployees)) return [];
    let list = approvedEmployees;
    if (statusFilter && statusFilter !== "all") {
      list = list.filter((emp) => emp?.status === statusFilter);
    }
    if (roleFilter && roleFilter !== "all") {
      list = list.filter((emp) => emp?.role === roleFilter);
    }
    if (zoneFilter && zoneFilter !== "all") {
      list = list.filter((emp) => {
        if (Array.isArray(emp?.workZone)) {
          return emp.workZone.some((z) => z?.id === zoneFilter || z?._id === zoneFilter);
        }
        return emp?.workZone?._id === zoneFilter;
      });
    }
    if (searchValue) {
      list = list.filter((emp) => searchEmployeeName(emp, searchValue));
    }
    return list;
  }, [approvedEmployees, searchValue, statusFilter, roleFilter, zoneFilter]);

  // add employee modal handlers

  // show add employee modal
  const handleAddEmployee = () => setShowAddModal(true);

  // close add employee modal
  const handleCloseAddModal = () => {
    dispatch(resetAddEmployeeForm());
    setShowAddModal(false);
  };
  //#endregion

  return (
    <div>

      {/* employee settings toolbar (search / filters) */}
      <Row className="align-items-center mb-3" style={{ marginRight:Spacing.SCALE_18, borderBottom: `2px solid lightgrey` }}>

        {/* search bar */}
        <Col>
          <TableHeader title={"Search"} />
          <SearchBar onChange={(e) => { const val = e.target.value; setSearchValue(val);
            }}
            placeholder="Search for an employee"
            value={searchValue}
          />
        </Col>

        {/* status drop down */}
        <Col>
            <TableHeader title={"Status"} />
            <EmployeeStatusFilter value={statusFilter} onChange={setStatusFilter} />
        </Col>

        {/* role drop down */}
        <Col>
          <TableHeader title={"Role"} />
          <EmployeeRoleFilter value={roleFilter} onChange={setRoleFilter} />
        </Col>

        {/* zone drop down */}
        <Col>
          <TableHeader title={"Zone"} />
          <div style={{ maxWidth: 150, minWidth: 150 }}>
            <EmployeeZoneFilter value={zoneFilter} onChange={setZoneFilter} maxWidth={150} />
          </div>
        </Col>

        {/* spacer */}
        <div style={{ marginTop: Spacing.SCALE_12, marginBottom: Spacing.SCALE_12 }} />

      </Row>

      {/* table headers 
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom: `2px solid ${Colors.BORDER_GRAY}` }}>
        <TableHeader title={"Employee"} />
        <TableHeader title={"Zone"} />
        <TableHeader title={""} />
      </div>
      */}

      {/* list of all employees */}
      <Row>
        <EmployeesListCard
          listTitle={"All Employees"}
          approvedEmployee={true}
          employees={filteredEmployees}
          assigningEmployee={true}
          handleSearch={setSearchValue}
        />
      </Row>

      <ActionsModal
        isTimeline={true}
        show={showAddModal}
        handleClose={handleCloseAddModal}
        title="Create Employee Account"
        subtitle={"To create an employee account enter the information below and click confirm"}
        minWidth="445px"
        maxWidth="445px"
      >
        <AddEmployeeForm closeModal={handleCloseAddModal} />
      </ActionsModal>

    </div>
  );
};

export default EmployeeSettings;
